/**
 * Secure postMessage Handler Utilities
 *
 * Provides origin validation for postMessage events to prevent
 * malicious sites from sending forged OAuth success messages.
 *
 * Security: Rule 2.1.1 - Server-Side Authorization
 * Issue: #5 - Missing CSRF Protection on postMessage
 */

/**
 * Get trusted origins based on environment configuration
 * @returns {string[]} Array of allowed origins
 */
export const getTrustedOrigins = () => {
  const origins = [window.location.origin];

  // Add server origin (for OAuth callbacks)
  if (process.env.REACT_APP_SERVER_ORIGIN) {
    origins.push(process.env.REACT_APP_SERVER_ORIGIN);
  }

  // Add custom trusted origins from environment (comma-separated list)
  // Example: REACT_APP_TRUSTED_ORIGINS=https://js.stripe.com,https://hooks.stripe.com
  if (process.env.REACT_APP_TRUSTED_ORIGINS) {
    const customOrigins = process.env.REACT_APP_TRUSTED_ORIGINS
      .split(',')
      .map(origin => origin.trim())
      .filter(origin => origin.length > 0);
    origins.push(...customOrigins);
  }

  // Development: allow localhost variants
  if (process.env.NODE_ENV === 'development') {
    origins.push('http://localhost:3000');
    origins.push('http://localhost:8080');
    origins.push('http://127.0.0.1:3000');
    origins.push('http://127.0.0.1:8080');
  }

  return origins;
};

/**
 * Validates postMessage event origin and structure
 * @param {MessageEvent} event - The message event
 * @param {string[]} allowedOrigins - Optional custom allowed origins
 * @returns {boolean} - True if message is valid and trusted
 */
export const validatePostMessage = (event, allowedOrigins = null) => {
  const trustedOrigins = allowedOrigins || getTrustedOrigins();

  // Validate origin exists and is trusted
  if (!event.origin || !trustedOrigins.includes(event.origin)) {
    console.warn('[Security] Rejected postMessage from untrusted origin:', event.origin);
    console.warn('[Security] Allowed origins:', trustedOrigins);
    return false;
  }

  // Validate data exists and is an object
  if (!event.data || typeof event.data !== 'object') {
    console.warn('[Security] Rejected postMessage with invalid data structure');
    return false;
  }

  return true;
};

/**
 * Creates a secure postMessage handler with automatic origin validation
 * @param {function} handler - Handler function to call for valid messages
 * @param {object} options - Configuration options
 * @param {string[]} options.allowedOrigins - Custom allowed origins (optional)
 * @param {string} options.expectedMessageType - Expected message type (optional)
 * @param {boolean} options.requireCSRFToken - Require CSRF token validation (optional)
 * @param {string} options.csrfToken - CSRF token to validate against (optional)
 * @returns {function} - Wrapped handler with origin validation
 */
export const createSecureMessageHandler = (handler, options = {}) => {
  const {
    allowedOrigins = null,
    expectedMessageType = null,
    requireCSRFToken = false,
    csrfToken = null
  } = options;

  return (event) => {
    // Phase 1: Validate origin and structure
    if (!validatePostMessage(event, allowedOrigins)) {
      return; // Reject message from untrusted origin
    }

    // Phase 2: Validate message type if specified
    if (expectedMessageType && event.data.type !== expectedMessageType) {
      console.warn('[Security] Unexpected message type:', event.data.type, 'Expected:', expectedMessageType);
      return; // Reject message with wrong type
    }

    // Phase 3: Validate CSRF token if required
    if (requireCSRFToken) {
      if (!event.data.csrfToken || event.data.csrfToken !== csrfToken) {
        console.warn('[Security] Invalid or missing CSRF token in postMessage');
        return; // Reject message without valid CSRF token
      }
    }

    // Message passed all validation checks - call handler
    handler(event);
  };
};

/**
 * Default export for convenience
 */
export default {
  getTrustedOrigins,
  validatePostMessage,
  createSecureMessageHandler
};
